<template>
  <div class="sp-hero" :class="classModifiers">
    <div ref="slidesContainer" class="sp-hero__slides">
      <slot />
    </div>

    <sp-hero-slideshow-navigation
      v-if="showNavigation"
      :slides.prop="slides"
      :autoplay.prop="autoplay"
      :current.prop="currentSlideIndex"
      :interval.prop="interval"
      @update-current="handleUpdateCurrent"
    ></sp-hero-slideshow-navigation>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useBreakpoints } from "../../composables/breakpoints";
import { toBoolean } from "../../utils/props";
import { useSlideshow } from "./composables/slideshow";

const props = defineProps({
  autoplay: {
    type: [Boolean, String],
    default: false,
  },
  interval: {
    type: [Number, String],
    required: false,
    default: undefined,
  },
  size: {
    type: String,
    validator: (value) => ["small", "medium", "large"].includes(value),
    default: "medium",
  },
  fullbleed: {
    type: [Boolean, String],
    default: false,
  },
  rounded: {
    type: [Boolean, String],
    default: false,
  },
});

const { isSmScreen, isMdScreen, isLgScreen } = useBreakpoints();
const slidesContainer = ref(null);
const { slides, currentSlideIndex, showNavigation, goToSlide } = useSlideshow(slidesContainer);

function handleUpdateCurrent({ detail }) {
  const [index] = detail;
  currentSlideIndex.value = index;
  goToSlide(index);
}

const classModifiers = computed(() => ({
  "--full-bleed": toBoolean(props.fullbleed),
  "--is-small-screen": isSmScreen.value,
  "--is-medium-screen": isMdScreen.value,
  "--is-large-screen": isLgScreen.value,
  "--rounded": toBoolean(props.rounded),
  [`--size-${props.size}`]: props.size,
  "--has-slideshow-navigation": showNavigation.value,
}));
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-hero {
  --height: var(--sp-comp-hero-height, auto);
  --min-height: var(--sp-comp-hero-min-height, auto);

  // Global item padding variables
  --slideshow-navigation-spacing: 0rem; // unit here is needed for the calc function
  --item-padding-slideshow-nav-offset: var(--slideshow-navigation-spacing);

  // Default item padding
  --item-padding-inline: var(--sp-comp-hero-item-padding-inline, var(--sp-ref-spacing-24));
  --item-padding-block-start: var(--sp-comp-hero-item-padding-block-start, 4.7rem);
  --item-padding-block-end: calc(
    var(--sp-comp-hero-item-padding-block-end, var(--item-padding-block-start)) +
      var(--item-padding-slideshow-nav-offset)
  );
  --item-padding-block: var(--item-padding-block-start) var(--item-padding-block-end);

  // Small screen (mobile) item padding
  --item-padding-inline-sm: var(--sp-comp-hero-item-padding-inline-sm, var(--sp-ref-spacing-12));
  --item-padding-block-sm-start: var(--sp-comp-hero-item-padding-block-sm-start, var(--sp-ref-spacing-10));
  --item-padding-block-sm-end: calc(
    var(--item-padding-slideshow-nav-offset) + var(--sp-comp-hero-item-padding-block-sm-end, var(--sp-ref-spacing-24))
  );
  --item-padding-block-sm: var(--item-padding-block-sm-start) var(--item-padding-block-sm-end);

  // Medium screen (tablet) item padding
  --item-padding-inline-md: var(--sp-comp-hero-item-padding-inline-md, var(--sp-ref-spacing-24));
  --item-padding-block-md-start: var(--sp-comp-hero-item-padding-block-md-start, var(--sp-ref-spacing-32));
  --item-padding-block-md-end: calc(
    var(--item-padding-slideshow-nav-offset) + var(--sp-comp-hero-item-padding-block-md-end, var(--sp-ref-spacing-40))
  );
  --item-padding-block-md: var(--item-padding-block-md-start) var(--item-padding-block-md-end);

  // Item background / border radius
  --item-background-width: var(--sp-comp-hero-item-background-width, 100%);
  --item-background-margin-inline: var(--sp-comp-hero-item-background-margin-inline, 0);
  --item-border-radius: var(--sp-comp-hero-item-border-radius, 0);

  position: relative;
  min-height: var(--min-height);
  height: var(--height);
  width: 100%;

  &.--size-small {
    --height: var(--sp-comp-hero-size-small-height, 21.25rem); // 340px

    &.--is-small-screen {
      --height: var(--sp-comp-hero-size-small-height-sm-screen, 26rem); // 416px
    }
  }

  &.--size-medium {
    --height: var(--sp-comp-hero-size-medium-height, 33.75rem); // 540px

    &.--is-small-screen {
      --height: var(--sp-comp-hero-size-medium-height-sm-screen, 33.75rem); // 540px
    }
  }

  &.--size-large {
    --height: var(--sp-comp-hero-size-large-height, 50rem); // 800px

    &.--is-small-screen {
      --height: var(--sp-comp-hero-size-large-height-sm-screen, 43.75rem); // 700px
    }
  }

  &.--has-slideshow-navigation {
    --slideshow-navigation-height: 2.625rem;
    --slideshow-navigation-bottom-offset: var(--sp-comp-hero-slideshow-navigation-bottom-offset, 5.5rem);
    --slideshow-navigation-spacing: calc(var(--slideshow-navigation-bottom-offset));

    &.--is-medium-screen {
      --slideshow-navigation-bottom-offset: var(--sp-comp-hero-slideshow-navigation-bottom-offset-sm-screen, 4rem);
    }

    &.--is-small-screen {
      --slideshow-navigation-bottom-offset: var(--sp-comp-hero-slideshow-navigation-bottom-offset-sm-screen, 2.5rem);
    }
  }

  &.--full-bleed {
    --item-padding-inline-sm: var(--sp-comp-hero-full-bleed-item-padding-inline-sm, 0);
    --item-padding-inline: var(--sp-comp-hero-full-bleed-item-padding-inline, 0);
    --item-padding-inline-md: var(--sp-comp-hero-full-bleed-item-padding-inline-md, 0);

    --item-border-radius: var(--sp-comp-hero-full-bleed-item-border-radius, 0);
    --item-background-width: var(--sp-comp-hero-full-bleed-item-background-width, 100vw);
    --item-background-margin-inline: calc(var(--item-background-width) / 2 * -1 + 50%);
  }

  &.--rounded {
    --item-border-radius: var(--sp-comp-hero-rounded-item-border-radius, var(--sp-sys-border-radius));
  }
}

.sp-hero__slides {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 1;
}
</style>
